import { DataGridPro, GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import { Typography } from '@mui/material';
import { McpToolInvocation } from '../../../Api/McpAccess';
import { DEFAULT_TABLE_STYLES } from '../UpstackDataGrid/UpstackDataGrid';
import { DD_GRID_CELL_SLOT_PROPS } from '../../../utils/telemetry';
import { formatLastUsed } from './formatLastUsed';
import { formatOptionalText } from './formatOptionalText';

export interface McpAccessAuditTableProps {
  invocations: McpToolInvocation[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPaginationModelChange: (page: number, pageSize: number) => void;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Server returns newest-first; sorting is disabled so we never re-order a single page.
const columns: GridColDef[] = [
  {
    field: 'created_at',
    headerName: 'Timestamp',
    sortable: false,
    flex: 1,
    valueFormatter: (value: string) => formatLastUsed(value)
  },
  { field: 'tool_name', headerName: 'Tool', sortable: false, flex: 1 },
  {
    field: 'client_name',
    headerName: 'Client',
    sortable: false,
    flex: 1,
    valueFormatter: (value: string | null) => formatOptionalText(value)
  }
];

const EmptyState = () => (
  <Typography
    variant="body2"
    color="text.secondary"
    data-testid="mcp-access-audit-empty"
    sx={{ p: 3, textAlign: 'center' }}>
    No activity recorded for this profile yet.
  </Typography>
);

export const McpAccessAuditTable = ({
  invocations,
  loading,
  page,
  pageSize,
  totalCount,
  onPaginationModelChange
}: McpAccessAuditTableProps) => {
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    onPaginationModelChange(model.page + 1, model.pageSize);
  };

  return (
    <DataGridPro
      rows={invocations}
      columns={columns}
      getRowId={(row) => row.id}
      loading={loading}
      autoHeight
      disableRowSelectionOnClick
      disableColumnFilter
      disableColumnMenu
      pagination
      paginationMode="server"
      rowCount={totalCount}
      paginationModel={{ page: page - 1, pageSize }}
      onPaginationModelChange={handlePaginationModelChange}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      columnHeaderHeight={DEFAULT_TABLE_STYLES.headerHeight}
      rowHeight={DEFAULT_TABLE_STYLES.rowHeight}
      sx={DEFAULT_TABLE_STYLES.sx}
      slots={{ noRowsOverlay: EmptyState }}
      slotProps={{ cell: DD_GRID_CELL_SLOT_PROPS }}
      data-testid="mcp-access-audit-grid"
    />
  );
};
